import type { ComponentType, ReactNode, SVGProps } from "react"

import {
  BoltIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  MapPinIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TicketIcon,
} from "@heroicons/react/24/outline"
import { clsx } from "clsx"

import { BatteryIcon } from "./icons/BatteryIcon"
import { GridIcon } from "./icons/GridIcon"
import { PanelIcon } from "./icons/PanelIcon"
import { PlugIcon } from "./icons/PlugIcon"

type IconName =
  | `battery`
  | `bolt`
  | `building`
  | `chart`
  | `check`
  | `clock`
  | `doc`
  | `grid`
  | `help`
  | `home`
  | `panel`
  | `pin`
  | `plug`
  | `shield`
  | `sparkle`
  | `ticket`
  | `warning`

const ICONS: Record<IconName, ComponentType<SVGProps<SVGSVGElement>>> = {
  battery: BatteryIcon,
  bolt: BoltIcon,
  building: BuildingOffice2Icon,
  chart: ChartBarIcon,
  check: CheckIcon,
  clock: ClockIcon,
  doc: DocumentTextIcon,
  grid: GridIcon,
  help: QuestionMarkCircleIcon,
  home: HomeIcon,
  panel: PanelIcon,
  pin: MapPinIcon,
  plug: PlugIcon,
  shield: ShieldCheckIcon,
  sparkle: SparklesIcon,
  ticket: TicketIcon,
  warning: ExclamationTriangleIcon,
}

export type Tint = `accent` | `cyan` | `danger` | `gold` | `success` | `text`

export const TINT_TEXT: Record<Tint, string> = {
  accent: `text-bp-accent`,
  gold: `text-bp-gold`,
  danger: `text-bp-danger`,
  success: `text-bp-success`,
  cyan: `text-bp-cyan`,
  text: `text-bp-text`,
}

export const TINT_BG: Record<Tint, string> = {
  accent: `bg-bp-accent`,
  gold: `bg-bp-gold`,
  danger: `bg-bp-danger`,
  success: `bg-bp-success`,
  cyan: `bg-bp-cyan`,
  text: `bg-bp-text`,
}

/** The raw custom property, for the handful of places that need a tint in an
 * inline style rather than a class — oversized ghost glyphs, gradient stops. */
export const TINT_VAR: Record<Tint, string> = {
  accent: `var(--bp-accent)`,
  gold: `var(--bp-gold)`,
  danger: `var(--bp-danger)`,
  success: `var(--bp-success)`,
  cyan: `var(--bp-cyan)`,
  text: `var(--bp-text)`,
}

export function Icon({
  name,
  className,
}: {
  name: IconName
  className?: string
}): JSX.Element | null {
  const Component = ICONS[name]
  return <Component className={clsx(`shrink-0`, className)} aria-hidden="true" />
}

export function Eyebrow({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <div
      className={clsx(
        `text-[length:var(--step--2)] font-semibold tracking-[0.18em] text-bp-text-muted uppercase`,
        className,
      )}
    >
      {children}
    </div>
  )
}

export function GradText({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="bg-[image:var(--bp-grad-accent)] bg-clip-text text-transparent">
      {children}
    </span>
  )
}

export function SectionHeading({
  eyebrow,
  children,
  sub,
}: {
  eyebrow?: ReactNode
  children: ReactNode
  sub?: string
}): JSX.Element {
  return (
    <div className="mb-9">
      {eyebrow ? <Eyebrow className="mb-3">{eyebrow}</Eyebrow> : null}
      <h2 className="font-display max-w-[820px] text-[length:var(--step-4)] leading-[1.05] font-semibold tracking-[-0.035em] text-bp-text">
        {children}
      </h2>
      {sub ? (
        <p className="mt-3.5 max-w-[640px] text-[length:var(--step-0)] leading-relaxed text-bp-text-muted">
          {sub}
        </p>
      ) : null}
    </div>
  )
}

export function Panel({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <div
      className={clsx(
        `rounded-lg border border-bp-border bg-bp-surface`,
        className,
      )}
    >
      {children}
    </div>
  )
}

/** Native-details accordion row — the compact mobile treatment for content
 * that renders as side-by-side cards on desktop. */
export function Disclosure({
  summary,
  children,
  defaultOpen = false,
}: {
  summary: ReactNode
  children: ReactNode
  defaultOpen?: boolean
}): JSX.Element {
  return (
    <details
      open={defaultOpen}
      className="group rounded-lg border border-bp-border bg-bp-surface"
    >
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3.5 select-none [&::-webkit-details-marker]:hidden">
        {summary}
        <ChevronDownIcon
          aria-hidden="true"
          className="ml-auto size-4 shrink-0 text-bp-text-muted transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="border-t border-bp-border px-4 py-4">{children}</div>
    </details>
  )
}

export function Section({
  id,
  children,
  className,
}: {
  id?: string
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <section
      id={id}
      className={clsx(`scroll-mt-24 px-6 pb-20 lg:px-16`, className)}
    >
      {children}
    </section>
  )
}
